import React, { useState, useRef, useEffect } from 'react';
import { submitAppFeedback } from '../services/api';

/**
 * AppFeedbackButton — Floating action button + popover for in-app feedback.
 * Renders fixed bottom-right on every authenticated page.
 *
 * Props:
 *   member  {Object|null}  Current member (used for display only; auth is always enforced)
 */
export default function AppFeedbackButton({ member }) {
  const [isOpen, setIsOpen] = useState(false);
  const [content, setContent] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [phase, setPhase] = useState('idle'); // 'idle'|'submitting'|'success'|'error'
  const [errorMsg, setErrorMsg] = useState(null);

  const cardRef = useRef(null);
  const btnRef = useRef(null);
  const successTimer = useRef(null);

  // Close on click-outside (exclude both card and the FAB itself)
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => {
      if (
        cardRef.current && !cardRef.current.contains(e.target) &&
        btnRef.current && !btnRef.current.contains(e.target)
      ) {
        handleClose();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen]);

  // Cleanup success timer on unmount
  useEffect(() => () => clearTimeout(successTimer.current), []);

  const handleClose = () => {
    setIsOpen(false);
    setErrorMsg(null);
    // Reset form after animation settles
    setTimeout(() => {
      setContent('');
      setIsAnonymous(false);
      setPhase('idle');
    }, 200);
  };

  const handleToggle = () => {
    if (isOpen) handleClose();
    else setIsOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (content.trim().length < 10 || phase === 'submitting') return;
    setPhase('submitting');
    setErrorMsg(null);
    try {
      await submitAppFeedback(content.trim(), isAnonymous);
      setPhase('success');
      successTimer.current = setTimeout(() => handleClose(), 1500);
    } catch (err) {
      setPhase('error');
      setErrorMsg(err.message || 'Something went wrong. Please try again.');
    }
  };

  const charCount = content.length;
  const isOverLimit = charCount > 1000;
  const isTooShort = content.trim().length < 10;
  const canSubmit = !isTooShort && !isOverLimit && phase !== 'submitting';

  const charColor = charCount >= 980
    ? 'var(--color-danger)'
    : charCount >= 900
    ? 'var(--accent)'
    : 'var(--text-faint)';

  return (
    <>
      {/* Popover card */}
      {isOpen && (
        <div
          ref={cardRef}
          style={{
            position: 'fixed',
            bottom: '80px',
            right: '24px',
            width: '320px',
            zIndex: 200,
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-accent)',
            borderTop: '3px solid var(--accent)',
            borderRadius: '14px',
            padding: '20px',
            boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
          }}
          className="app-feedback-card"
        >
          {phase === 'success' ? (
            <div className="flex flex-col items-center gap-3 py-4">
              <div style={{
                width: '44px', height: '44px', borderRadius: '50%',
                background: 'var(--color-success-dim)', border: '1px solid var(--color-success-border)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M4 10l4 4 8-8" stroke="var(--color-success)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <p className="text-sm font-medium text-center" style={{ color: 'var(--color-success)' }}>
                Thanks for the feedback!
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-semibold" style={{ color: 'var(--text-primary)', fontSize: '17px' }}>
                  Share a thought
                </h3>
                <button
                  type="button"
                  onClick={handleClose}
                  style={{ color: 'var(--text-faint)', background: 'none', border: 'none', cursor: 'pointer', padding: '2px' }}
                  onMouseEnter={e => e.currentTarget.style.color = 'var(--text-secondary)'}
                  onMouseLeave={e => e.currentTarget.style.color = 'var(--text-faint)'}
                  aria-label="Close"
                >
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M4 4l10 10M14 4L4 14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  </svg>
                </button>
              </div>

              {/* Textarea */}
              <div className="relative mb-3">
                <textarea
                  value={content}
                  onChange={(e) => { setContent(e.target.value); setErrorMsg(null); if (phase === 'error') setPhase('idle'); }}
                  placeholder="What's on your mind? Bug, idea, or just a thought…"
                  rows={4}
                  disabled={phase === 'submitting'}
                  style={{
                    width: '100%',
                    background: 'var(--bg-input)',
                    border: `1px solid ${isOverLimit ? 'var(--color-danger)' : 'var(--border-accent)'}`,
                    borderRadius: '8px',
                    color: 'var(--text-primary)',
                    fontSize: '13px',
                    padding: '10px 12px',
                    resize: 'vertical',
                    minHeight: '96px',
                    outline: 'none',
                    lineHeight: '1.5',
                    boxSizing: 'border-box',
                  }}
                  onFocus={e => e.target.style.borderColor = isOverLimit ? 'var(--color-danger)' : 'var(--border-accent)'}
                  onBlur={e => e.target.style.borderColor = isOverLimit ? 'var(--color-danger)' : 'var(--border-accent)'}
                />
                {/* Character counter */}
                <span style={{
                  position: 'absolute',
                  bottom: '8px',
                  right: '10px',
                  fontSize: '13px',
                  color: charColor,
                  pointerEvents: 'none',
                }}>
                  {charCount}/1000
                </span>
              </div>

              {/* Anonymous checkbox */}
              <label className="flex items-center gap-2 mb-4 cursor-pointer select-none" style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                <input
                  type="checkbox"
                  checked={isAnonymous}
                  onChange={(e) => setIsAnonymous(e.target.checked)}
                  disabled={phase === 'submitting'}
                  style={{ accentColor: 'var(--accent)', width: '14px', height: '14px', cursor: 'pointer' }}
                />
                Send anonymously
              </label>

              {/* Error message */}
              {phase === 'error' && errorMsg && (
                <p className="text-xs mb-3 px-3 py-2 rounded-lg" style={{ background: 'var(--color-danger-dim)', border: '1px solid var(--color-danger-border)', color: 'var(--color-danger)' }}>
                  {errorMsg}
                </p>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={!canSubmit}
                className="btn-bouncy w-full"
                style={{ fontSize: '13px', padding: '9px 16px', opacity: canSubmit ? 1 : 0.4, cursor: canSubmit ? 'pointer' : 'not-allowed' }}
              >
                {phase === 'submitting' ? 'Sending…' : 'Send feedback'}
              </button>
            </form>
          )}
        </div>
      )}

      {/* Mobile bottom-sheet styles */}
      <style>{`
        @media (max-width: 639px) {
          .app-feedback-card {
            bottom: 0 !important;
            right: 0 !important;
            left: 0 !important;
            width: 100% !important;
            border-radius: 16px 16px 0 0 !important;
          }
        }
      `}</style>

      {/* FAB */}
      <button
        ref={btnRef}
        onClick={handleToggle}
        aria-label="Share feedback"
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          zIndex: 200,
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, var(--accent) 0%, #e8941a 100%)',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 16px var(--accent-glow)',
          transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.boxShadow = '0 6px 24px var(--accent-glow)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 16px var(--accent-glow)'; }}
        onMouseDown={e => e.currentTarget.style.transform = 'scale(0.95)'}
        onMouseUp={e => e.currentTarget.style.transform = 'scale(1.1)'}
      >
        {/* Speech bubble icon */}
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
            fill="rgba(13,11,7,0.85)"
          />
          <circle cx="8.5" cy="11" r="1" fill="rgba(13,11,7,0.85)" />
          <circle cx="12" cy="11" r="1" fill="rgba(13,11,7,0.85)" />
          <circle cx="15.5" cy="11" r="1" fill="rgba(13,11,7,0.85)" />
        </svg>
      </button>
    </>
  );
}
